import {
  RECHARGE_TOPICS,
  RECHARGE_WEBHOOK_OUTCOME,
  rechargeWebhookOutcomeSchema,
  statusEntries,
  type RechargeWebhookOutcome,
} from '@flux/shared';
import { X } from 'lucide-react';
import type { JSX } from 'react';
import { z } from 'zod';
import { Button } from '@/components/ui/button.js';
import { MultiSelectFilter, PeriodFilter, type MultiSelectFilterOption } from './filter-controls.js';

const TOPIC_OPTIONS: readonly MultiSelectFilterOption<string>[] = RECHARGE_TOPICS.map((topic) => ({
  value: topic,
  label: (
    <span className="font-mono text-xs" translate="no">
      {topic}
    </span>
  ),
}));

const OUTCOME_OPTIONS: readonly MultiSelectFilterOption<RechargeWebhookOutcome>[] = statusEntries(
  RECHARGE_WEBHOOK_OUTCOME,
).map((entry) => ({ value: entry.code, label: entry.label }));

export interface WebhookFiltersValue {
  topic: string[];
  outcome: RechargeWebhookOutcome[];
  /** ISO-8601 UTC, début du jour parisien choisi. */
  from?: string;
  /** ISO-8601 UTC, fin du jour parisien choisi. */
  to?: string;
}

export type WebhookFiltersPatch = Partial<{
  topic: string[] | undefined;
  outcome: RechargeWebhookOutcome[] | undefined;
  from: string | undefined;
  to: string | undefined;
}>;

/** Patch qui retire tous les filtres de l'onglet (« Réinitialiser »). */
export const CLEAR_WEBHOOK_FILTERS: WebhookFiltersPatch = {
  topic: undefined,
  outcome: undefined,
  from: undefined,
  to: undefined,
};

function readRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

const isoDateTime = z.iso.datetime();

/** Lit les filtres de l'onglet Webhooks depuis `useSearch({strict:false})` (`06` §8.5). */
export function readWebhookFilters(rawSearch: unknown): WebhookFiltersValue {
  const search = readRecord(rawSearch);
  const topic = z.array(z.string().min(1)).safeParse(search.topic);
  const outcome = z.array(rechargeWebhookOutcomeSchema).safeParse(search.outcome);
  const from = isoDateTime.safeParse(search.from);
  const to = isoDateTime.safeParse(search.to);
  return {
    topic: topic.success ? topic.data : [],
    outcome: outcome.success ? outcome.data : [],
    ...(from.success ? { from: from.data } : {}),
    ...(to.success ? { to: to.data } : {}),
  };
}

export function hasActiveWebhookFilters(value: WebhookFiltersValue): boolean {
  return (
    value.topic.length > 0 ||
    value.outcome.length > 0 ||
    value.from !== undefined ||
    value.to !== undefined
  );
}

export interface WebhookFiltersProps {
  value: WebhookFiltersValue;
  onChange: (patch: WebhookFiltersPatch) => void;
}

/**
 * Barre de filtres de l'onglet Webhooks (`06` §8.5) : topic, résultat,
 * période. Même gabarit que `ListToolbar` (« Réinitialiser » en `ghost`), sans
 * recherche libre : la spec n'en prévoit pas pour ce journal.
 */
export function WebhookFilters({ value, onChange }: WebhookFiltersProps): JSX.Element {
  return (
    <div className="flex w-full flex-wrap items-center gap-2">
      <MultiSelectFilter
        label="Topic"
        ariaLabel="Filtrer par topic"
        options={TOPIC_OPTIONS}
        value={value.topic}
        onChange={(topic) => {
          onChange({ topic });
        }}
      />
      <MultiSelectFilter
        label="Résultat"
        ariaLabel="Filtrer par résultat"
        options={OUTCOME_OPTIONS}
        value={value.outcome}
        onChange={(outcome) => {
          onChange({ outcome });
        }}
      />
      <PeriodFilter
        from={value.from}
        to={value.to}
        fromLabel="Reçu depuis le"
        toLabel="Reçu jusqu’au"
        onChange={onChange}
      />

      {hasActiveWebhookFilters(value) ? (
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            onChange(CLEAR_WEBHOOK_FILTERS);
          }}
        >
          <X aria-hidden="true" />
          Réinitialiser
        </Button>
      ) : null}
    </div>
  );
}
